import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { FiMapPin, FiPhone, FiMail, FiPackage, FiAlertCircle } from 'react-icons/fi';
import { dealerService } from '../services/dealerService';

// Fix default marker icons (Leaflet + bundlers issue)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const activeIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const selectedIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

// City-to-coordinates lookup for dealers without lat/lng fields
const CITY_COORDS = {
  'dallas': { lat: 32.7767, lng: -96.7970 },
  'fort worth': { lat: 32.7555, lng: -97.3308 },
  'austin': { lat: 30.2672, lng: -97.7431 },
  'houston': { lat: 29.7604, lng: -95.3698 },
  'san antonio': { lat: 29.4241, lng: -98.4936 },
  'plano': { lat: 33.0198, lng: -96.6989 },
  'waco': { lat: 31.5493, lng: -97.1467 },
  'tyler': { lat: 32.3513, lng: -95.3011 },
  'amarillo': { lat: 35.2220, lng: -101.8313 },
  'lubbock': { lat: 33.5779, lng: -101.8552 },
  'el paso': { lat: 31.7619, lng: -106.4850 },
  'corpus christi': { lat: 27.8006, lng: -97.3964 },
  'mcallen': { lat: 26.2034, lng: -98.2300 },
  'midland': { lat: 31.9973, lng: -102.0779 },
  'arlington': { lat: 32.7357, lng: -97.1081 },
  'frisco': { lat: 33.1507, lng: -96.8236 },
  'mckinney': { lat: 33.1972, lng: -96.6397 },
  'denton': { lat: 33.2148, lng: -97.1331 },
  'bismarck': { lat: 46.8083, lng: -100.7837 },
  'rapid city': { lat: 44.0805, lng: -103.2310 },
  'denver': { lat: 39.7392, lng: -104.9903 },
  'colorado springs': { lat: 38.8339, lng: -104.8214 },
  'sacramento': { lat: 38.5816, lng: -121.4944 },
  'baton rouge': { lat: 30.4515, lng: -91.1871 },
  'new orleans': { lat: 29.9511, lng: -90.0715 },
  'albuquerque': { lat: 35.0844, lng: -106.6504 },
  'oklahoma city': { lat: 35.4676, lng: -97.5164 },
  'tulsa': { lat: 36.1540, lng: -95.9928 },
  'little rock': { lat: 34.7465, lng: -92.2896 },
  'memphis': { lat: 35.1495, lng: -90.0490 },
  'nashville': { lat: 36.1627, lng: -86.7816 },
  'jackson': { lat: 32.2988, lng: -90.1848 },
  'shreveport': { lat: 32.5252, lng: -93.7502 },
  'beaumont': { lat: 30.0802, lng: -94.1266 },
  'abilene': { lat: 32.4487, lng: -99.7331 },
  'temple': { lat: 31.0982, lng: -97.3428 },
  'killeen': { lat: 31.1171, lng: -97.7278 },
  'college station': { lat: 30.6280, lng: -96.3344 },
  'bryan': { lat: 30.6744, lng: -96.3700 },
  'laredo': { lat: 27.5036, lng: -99.5076 },
  'brownsville': { lat: 25.9017, lng: -97.4975 },
};

// Fly to selected dealer
function FlyToDealer({ dealer }) {
  const map = useMap();
  useEffect(() => {
    if (dealer) {
      map.flyTo([dealer.lat, dealer.lng], 10, { duration: 1 });
    }
  }, [dealer, map]);
  return null;
}

const DealerMap = ({ compact = false, className = '' }) => {
  const [dealers, setDealers] = useState([]);
  const [selectedDealer, setSelectedDealer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const mapRef = useRef(null);

  // Load real dealers from Supabase
  useEffect(() => {
    const loadDealers = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: fetchError } = await dealerService.getAllDealers();
        if (fetchError) {
          console.error('Failed to load dealers:', fetchError);
          setError('Unable to load dealer locations right now.');
          setDealers([]);
          return;
        }

        if (!data || data.length === 0) {
          setDealers([]);
          return;
        }

        // Map dealers to include coordinates
        const mapped = data
          .filter(d => d.is_active)
          .map(d => {
            const cityKey = (d.city || '').toLowerCase().trim();
            const coords = CITY_COORDS[cityKey];
            if (!coords) return null;
            return {
              ...d,
              lat: coords.lat,
              lng: coords.lng,
              radius_miles: 30,
            };
          })
          .filter(Boolean);

        setDealers(mapped);
      } catch (err) {
        console.error('Error loading dealers:', err);
        setError('Unable to load dealer locations right now.');
        setDealers([]);
      } finally {
        setLoading(false);
      }
    };
    loadDealers();
  }, []);

  const milesToMeters = (miles) => miles * 1609.34;

  const planBadge = (plan) => {
    const colors = {
      enterprise: 'bg-purple-100 text-purple-700',
      pro: 'bg-blue-100 text-blue-700',
      basic: 'bg-gray-100 text-gray-600',
    };
    return colors[plan] || colors.basic;
  };

  // Empty state for when no dealers are loaded
  const EmptyState = () => (
    <div className={`flex flex-col items-center justify-center p-8 bg-white rounded-lg border border-gray-200 ${className}`} style={{ minHeight: compact ? 300 : 400 }}>
      <FiMapPin className="w-12 h-12 text-gray-300 mb-4" />
      <h3 className="text-lg font-semibold text-gray-700 mb-2">No Dealers Found</h3>
      <p className="text-gray-500 text-sm text-center max-w-sm">
        {error || "We're expanding our dealer network. Check back soon or contact us for availability in your area."}
      </p>
    </div>
  );

  if (!loading && dealers.length === 0) {
    return <EmptyState />;
  }

  if (compact) {
    return (
      <div className={`rounded-lg overflow-hidden border border-gray-200 ${className}`}>
        {loading && (
          <div className="flex items-center justify-center bg-gray-50" style={{ minHeight: 300 }}>
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
          </div>
        )}
        {!loading && dealers.length > 0 && (
          <MapContainer
            center={[35, -98]}
            zoom={4}
            scrollWheelZoom={false}
            style={{ height: '100%', width: '100%', minHeight: 300 }}
            zoomControl={false}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {dealers.map((dealer) => (
              <React.Fragment key={dealer.id}>
                <Circle
                  center={[dealer.lat, dealer.lng]}
                  radius={milesToMeters(dealer.radius_miles)}
                  pathOptions={{
                    color: '#059669',
                    fillColor: '#059669',
                    fillOpacity: 0.08,
                    weight: 1,
                  }}
                />
                <Marker position={[dealer.lat, dealer.lng]} icon={activeIcon}>
                  <Popup>
                    <strong>{dealer.company_name}</strong>
                    <br />
                    {dealer.city}, {dealer.state}
                  </Popup>
                </Marker>
              </React.Fragment>
            ))}
          </MapContainer>
        )}
      </div>
    );
  }

  return (
    <div className={`flex flex-col lg:flex-row gap-0 bg-white rounded-lg overflow-hidden border border-gray-200 ${className}`}>
      {/* Sidebar */}
      <div className="w-full lg:w-80 xl:w-96 flex-shrink-0 border-r border-gray-200 flex flex-col" style={{ maxHeight: 600 }}>
        <div className="p-4 border-b border-gray-100 bg-gray-50">
          <div className="text-sm text-gray-500">
            <span className="font-semibold text-gray-900 text-lg">{dealers.length}</span> dealer{dealers.length !== 1 ? 's' : ''} in network
          </div>
        </div>
        <div className="overflow-y-auto flex-1">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
            </div>
          )}
          {!loading && dealers.map((dealer) => (
            <button
              key={dealer.id}
              onClick={() => setSelectedDealer(dealer)}
              className={`w-full text-left px-4 py-3.5 border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                selectedDealer?.id === dealer.id ? 'bg-emerald-50 border-l-2 border-l-emerald-600' : ''
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-medium text-gray-900 text-sm truncate">{dealer.company_name}</div>
                  <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
                    <FiMapPin className="w-3 h-3" />
                    {dealer.city}, {dealer.state}
                  </div>
                </div>
                {dealer.subscription_plan && (
                  <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${planBadge(dealer.subscription_plan)}`}>
                    {dealer.subscription_plan}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-400">
                <span>{dealer.radius_miles} mi radius</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Map */}
      <div className="flex-1 relative" style={{ minHeight: 600 }}>
        {loading && (
          <div className="absolute inset-0 bg-white/80 z-[1000] flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
          </div>
        )}
        <MapContainer
          ref={mapRef}
          center={[35, -98]}
          zoom={5}
          scrollWheelZoom={true}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <FlyToDealer dealer={selectedDealer} />
          {dealers.map((dealer) => (
            <React.Fragment key={dealer.id}>
              <Circle
                center={[dealer.lat, dealer.lng]}
                radius={milesToMeters(dealer.radius_miles)}
                pathOptions={{
                  color: selectedDealer?.id === dealer.id ? '#dc2626' : '#059669',
                  fillColor: selectedDealer?.id === dealer.id ? '#dc2626' : '#059669',
                  fillOpacity: selectedDealer?.id === dealer.id ? 0.15 : 0.06,
                  weight: selectedDealer?.id === dealer.id ? 2 : 1,
                }}
              />
              <Marker
                position={[dealer.lat, dealer.lng]}
                icon={selectedDealer?.id === dealer.id ? selectedIcon : activeIcon}
                eventHandlers={{
                  click: () => setSelectedDealer(dealer),
                }}
              >
                <Popup>
                  <div className="min-w-[200px]">
                    <div className="font-semibold text-gray-900 text-sm">{dealer.company_name}</div>
                    <div className="text-xs text-gray-500 mt-1 mb-2">{dealer.city}, {dealer.state}</div>
                    <div className="space-y-1 text-xs text-gray-600">
                      {dealer.phone && (
                        <div className="flex items-center gap-1.5">
                          <FiPhone className="w-3 h-3" />
                          {dealer.phone}
                        </div>
                      )}
                      {dealer.email && (
                        <div className="flex items-center gap-1.5">
                          <FiMail className="w-3 h-3" />
                          {dealer.email}
                        </div>
                      )}
                    </div>
                    <div className="mt-2 pt-2 border-t border-gray-100 text-xs text-gray-400">
                      Coverage: {dealer.radius_miles} mile radius
                    </div>
                  </div>
                </Popup>
              </Marker>
            </React.Fragment>
          ))}
        </MapContainer>
      </div>
    </div>
  );
};

export default DealerMap;
